import type { ReactNode } from 'react';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';

interface ToggleSettingCardProps {
  label: string;
  hint?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  icon?: ReactNode;
  children?: ReactNode;
  // Set when this same flag already has its own control elsewhere (e.g. the Production section's
  // master Switch lives in its SectionCard header instead) — `checked` still drives this card's
  // dimmed/enabled look, it just doesn't duplicate the interactive Switch here too.
  hideSwitch?: boolean;
  // Set when a higher-level master switch (e.g. Production's own) is off — makes this card's
  // Switch non-interactive and dims it regardless of `checked`, since its own flag is moot while
  // the step it belongs to is switched off entirely.
  disabled?: boolean;
}

export default function ToggleSettingCard({ label, hint, checked, onChange, icon, children, hideSwitch, disabled }: ToggleSettingCardProps) {
  return (
    <Card variant="outlined" sx={{ opacity: checked && !disabled ? 1 : 0.6, transition: 'opacity 0.2s' }}>
      <CardContent>
        {hideSwitch ? (
          icon
        ) : (
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            {icon}
            <Switch checked={checked} onChange={(e) => onChange(e.target.checked)} disabled={disabled} sx={{ ml: 'auto' }} />
          </Stack>
        )}
        <Typography fontWeight={600} sx={{ mt: icon ? 1 : 0 }}>
          {label}
        </Typography>
        {hint ? (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {hint}
          </Typography>
        ) : undefined}
        {children}
      </CardContent>
    </Card>
  );
}
